import * as three from 'three';

import { ID, useAsync, useClientData, useJeffect, useLogger } from "@kosmod/kit";
import { ObjectClone, renderBeat, useDisposable } from "@kosmod/graphics-3d";
import { h, useMemo, useRef, useState } from "@lukekaalim/act";
import { a3 } from "@lukekaalim/act-three";
import { html } from "@lukekaalim/act-web";

import { findPath } from "../../tools/pathfinding";
import { assetsURLs } from "../../assets";

import { defineToolHandler } from "./handler";
import { AnyLogicState } from "../../defs/logic/all";
import { Logic, TheatrePosition, tools } from "../../defs";
import { MiniModelPreview } from '../Theatre/Mini/Preview';
import { CursorSurfacePreview } from './Preview';
import { MiniGameMatRenderer } from '../Theatre/Mini/GameMat';


defineToolHandler(tools.mini.place, {
  def: tools.mini.place,
  onInput(event, state, theatre) {
    if (event.type === 'mouse' && event.button === 'primary') {
      const position = theatre.cursorPosition.get();
      if (position) {
        if (state.source.type !== 'template')
          return;
        
        const template = theatre.data.miniTemplates.get(state.source.miniTemplateId);
        if (!template)
          return;

        const logic = template.logic.map(Logic.clone);

        const character = Logic.find('character', logic);
        if (character && state.placementCounter > 0) {
          character.data.name += ` #${state.placementCounter}`;
        }

        theatre.controller.addMini(TheatrePosition.snapToSquareGrid(position), logic);
        theatre.tools.setToolState(state.id, { ...state, placementCounter: state.placementCounter + 1 })
      }
    }
  },
  PreviewComponent({ cursorPosition, toolState, theatre, material }) {
    const onRender = renderBeat.useBeat();
    const ref = useRef<null | three.Object3D>(null);
    const ref2 = useRef<null | three.Object3D>(null);

    if (toolState.source.type !== 'template')
      return null;
    
    const template = theatre.data.miniTemplates.get(toolState.source.miniTemplateId);
    if (!template)
      return null;

    const renderLogic = Logic.find('render', template.logic);
    if (!renderLogic)
      return null;

    if (renderLogic.data.renderType === 'model') {
      const model = renderLogic.data.modelId && theatre.sceneRes.miniModels.get(renderLogic.data.modelId);

      return h(CursorSurfacePreview, { state: theatre, snap: 'square' }, [
        !!model && h(ObjectClone, { target: model, material, position: new three.Vector3(.5, .5, 0) })
      ])
    }
    if (renderLogic.data.renderType === 'game-mat') {
      return h(CursorSurfacePreview, { state: theatre, snap: 'square' }, [
        h(MiniGameMatRenderer, {
          mini: {
            id: ID('mini'),
            logic: template.logic,
            networkState: 'ok',
            animation: null,
            position: {
              type: 'grid',
              vector: { x: 0, y: 0 },
              surfaceId: ID('surface')
            },
            rotation: 0,
          },
          render: renderLogic.data,
          res: theatre.sceneRes,
        })
      ])
    }
    
    const spriteId = renderLogic.data.renderType === 'quick-sprite' && renderLogic.data.spriteId;
    if (!spriteId)
      return null;

    const texture = theatre.sceneRes.miniSprites.get(spriteId);
    if (!texture)
      return null;

    useJeffect(j => {
      const mesh = ref.get();
      const mesh2 = ref2.get();
      if (!mesh || !mesh2)
        return;

      const camera = theatre.camera.get();
      if (!camera)
        return;
      
      j.add(onRender(() => {
        const position = cursorPosition.get();
        const rotationOffset = new three.Vector2(
          camera.forward.x * camera.vertical * 0.1,
          camera.forward.y * camera.vertical * 0.1
        ).clampLength(0, 0.5)

        if (position) {
          const gridPosition = TheatrePosition.snapToSquareGrid(position);
          const destinationVector = TheatrePosition.copyToVector3(gridPosition);

          // theatre.data.walkable.read(gridPosition.vector.x, gridPosition.vector.y);

          mesh2.position
            .copy(destinationVector)
            .add({ x: .5, y: 0, z: .5 })
          mesh.position
            .copy(destinationVector)
            .add({ x: rotationOffset.x, z: rotationOffset.y, y: 0 })
            .add({ x: .5, y: 0, z: .5 })
        }
      }))
    }, [texture])

    if (!texture)
      return null;

    const spriteMaterial = useDisposable(() => {
      return new three.SpriteMaterial({ map: texture, alphaHash: true, alphaTest: 0.2, color: new three.Color('#ffffff') })
    }, [texture])
    const geometry = useDisposable(() => {
      return new three.BoxGeometry(1, 0.05, 1);
    }, [texture])

    return [
      h(a3.sprite, {
        ref,
        material: spriteMaterial,
        center: { x: 0.5, y: 0 },
        scale: new three.Vector3(texture.width/texture.height, 1, 0),
     }),
     h(a3.mesh, { geometry, ref: ref2 })
    ];
  },
  ToolbeltComponent({ toolState, theatre }) {
    if (toolState.source.type !== 'template')
      return null;
    
    const template = theatre.data.miniTemplates.get(toolState.source.miniTemplateId);
    if (!template)
      return null;

    const renderLogic = Logic.find('render', template.logic);

    if (!renderLogic)
      return 'No Render Logic';

    return [
      renderLogic.data.renderType === 'quick-sprite' && renderLogic.data.spriteId && h(html.img, {
        src: theatre.sceneRes.miniSpritesURLs.get(renderLogic.data.spriteId)?.href || ''
      }),
      renderLogic.data.renderType === 'model' && renderLogic.data.modelId && h(MiniModelPreview, {
        miniModel: theatre.content.miniModels.get(renderLogic.data.modelId) || null,
        graphics: theatre.graphics,
        resources: theatre.sceneRes,
      })
    ]
  }
});

defineToolHandler(tools.mini.move, {
  def: tools.mini.move,

  onInput(event, state, theatre, logger, nativeEvent) {
    if (event.type === 'mouse' && event.button === 'secondary') {
      theatre.selection.select({ type: 'none' });
      nativeEvent.preventDefault();
      return;
    }
    if (event.type === 'mouse' && event.button === 'primary') {
      const position = theatre.cursorPosition.get();
      const miniClient = theatre.data.minis.find(c => c.current.id === state.miniId);
      if (position && miniClient) {
        const mini = miniClient.current;
        const gridPosition = TheatrePosition.snapToSquareGrid(position);
        const movement = Logic.find('movement', mini.logic);

        if (movement && movement.data.method === 'none') {
          theatre.controller.moveMini(mini.id, gridPosition)
          return;
        }

        const surfaceData = theatre.surfaceData.get(mini.position.surfaceId);
        
        const path = surfaceData && findPath(
          surfaceData.walkable,
          new three.Vector2(mini.position.vector.x, mini.position.vector.y),
          new three.Vector2(gridPosition.vector.x, gridPosition.vector.y)
        )
        theatre.controller.moveMini(
          mini.id,
          gridPosition,
          path && { start: Date.now(), points: path.map(p => ({ ...gridPosition, vector: { x: p.x, y: p.y } }))}
        )
      }
    }
  },
  ToolbeltComponent() {
    return h(html.img, { src: assetsURLs.icons.move })
  },

  PreviewComponent({ theatre, toolState, material = new three.MeshBasicMaterial({ color: '#95c6e7' }), cursorPosition }) {
    const logger = useLogger('MiniMoveTool')
    const miniClient = theatre.data.minis.find(c => c.current.id === toolState.miniId);


    if (!miniClient)
      return null;

    const mini = useClientData(miniClient);
    const [target, setTarget] = useState<three.Vector2 | null>(null)

    const path = useMemo(() => {
      const surfaceData = theatre.surfaceData.get(mini.position.surfaceId);
      if (!surfaceData || !target)
        return null
      return findPath(surfaceData.walkable, new three.Vector2(mini.position.vector.x, mini.position.vector.y), target)
    }, [target, theatre.surfaceData, mini.position.surfaceId, mini.position.vector]);

    renderBeat.useBeat(() => {
      const cursor = cursorPosition.get();
      if (!cursor)
        return;
  
      const roundedCursor = new three.Vector2(cursor.vector.x, cursor.vector.y)
        .addScalar(-0.5)
        .round();
  
      setTarget(t => t && t.equals(roundedCursor) ? t : roundedCursor)
    });
  
    if (!path)
      return null;
  
    return path.map(segment => {
      return h(a3.mesh, { geometry: planeGeo, material, position: { x: segment.x + 0.5, y: 0.1, z: segment.y + 0.5 } });
    })
  }
})

defineToolHandler(tools.mini.delete, {
  def: tools.mini.delete,
  onSelect(state, tools, selection, logger, controller, data) {
    controller.removeMini(state.miniId);
    selection.select({ type: 'none' });
  },
  ToolbeltComponent({ toolState, theatre }) {
    return h(html.img, { src: assetsURLs.icons.trash })
  },
});
const planeGeo = new three.PlaneGeometry(.8, .8).rotateX(Math.PI * -0.5);
